import { Injectable } from "@angular/core";
import { combineLatest, map, startWith } from "rxjs";
import { UiQuery } from "../state/ui.query";
import { UiService } from "../state/ui.service";
import { LocationStateQuery } from "../state/location-state.query";
import { FeatureFlagService } from "source/app/features/feature-flag/services/feature-flag.service";
import { RIDEOPS_UI_V2 } from "source/app/features/feature-flag/feature-flag.constants";
import { NoteEditorComponentMode } from "source/app/modals/note-editor/note-editor.mode";
import { AssetDispatchService } from "./asset-dispatch.service";

@Injectable({ providedIn: "root" })
export class RideOpsUiService {
	constructor(
		private featureFlagService: FeatureFlagService,
		private uiQuery: UiQuery,
		private uiService: UiService,
		private locationStateQuery: LocationStateQuery,
		private assetDispatchService: AssetDispatchService,
	) {
		this.persistFlagAvailabilityWhileSignedIn();
		this.persistOptInWhenForcedByAssetMode();
	}

	/**
	 * Live CORE flag when an operator is signed in; otherwise the last value persisted to the
	 * UI store (localStorage). Flags are cleared on logout, so without this cache `/operators`
	 * would always fall back to v1 after sign-out.
	 */
	isV2Available$ = combineLatest([
		this.featureFlagService.flags$,
		this.locationStateQuery.selectOperator$,
		this.uiQuery.rideOpsUiV2FlagAvailable$,
	]).pipe(
		map(([flags, operator, lastKnownAvailable]) => {
			if (operator == null || flags.length === 0) {
				return lastKnownAvailable;
			}
			return this.featureFlagService.isEnabled(RIDEOPS_UI_V2);
		}),
		startWith(this.uiQuery.getRideOpsUiV2FlagAvailable()),
	);

	isV2OptIn$ = this.uiQuery.rideOpsUiV2OptIn$;

	/** Asset-mode locations have no v1 dispatch UI — force v2 while that gate is on. */
	isV2ForcedByAssetMode$ = this.assetDispatchService.assetBasedDispatchEnabled$;

	isV2Active$ = combineLatest([this.isV2Available$, this.isV2OptIn$, this.isV2ForcedByAssetMode$]).pipe(
		map(([available, optIn, forcedByAsset]) => available && (optIn || forcedByAsset)),
	);

	isV2Active = (): boolean => {
		return this.isV2Available() && (this.uiQuery.getRideOpsUiV2OptIn() || this.assetDispatchService.isAssetBasedDispatchEnabled());
	};

	isV2ForcedByAssetMode = (): boolean => this.assetDispatchService.isAssetBasedDispatchEnabled();

	noteEditorModalCssClass = (mode: NoteEditorComponentMode): string => {
		if (!this.isV2Active()) {
			return "note-editor-modal auto-height";
		}

		const create = mode === NoteEditorComponentMode.Create ? " note-editor-modal-v2--create" : "";
		return `note-editor-modal note-editor-modal-v2${create} auto-height`;
	};

	downLocationModalCssClass = (): string =>
		this.isV2Active()
			? "down-location-modal down-location-modal-v2 auto-height"
			: "down-location-modal auto-height";

	private isV2Available = (): boolean => {
		const operator = this.locationStateQuery.getSnapshot().operator;
		if (operator == null || this.featureFlagService.getFlags().length === 0) {
			return this.uiQuery.getRideOpsUiV2FlagAvailable();
		}
		return this.featureFlagService.isEnabled(RIDEOPS_UI_V2);
	};

	private persistFlagAvailabilityWhileSignedIn = () => {
		combineLatest([this.featureFlagService.flags$, this.locationStateQuery.selectOperator$]).subscribe(
			([flags, operator]) => {
				if (operator == null) {
					return;
				}
				// Skip the empty post-clear / pre-load window so we do not wipe a good cache.
				if (flags.length === 0) {
					return;
				}

				const available = this.featureFlagService.isEnabled(RIDEOPS_UI_V2);
				if (available === this.uiQuery.getRideOpsUiV2FlagAvailable()) {
					return;
				}
				this.uiService.setRideOpsUiV2FlagAvailable(available);
			},
		);
	};

	private persistOptInWhenForcedByAssetMode = () => {
		combineLatest([this.isV2ForcedByAssetMode$, this.locationStateQuery.selectOperator$]).subscribe(
			([forced, operator]) => {
				if (operator == null || forced !== true) {
					return;
				}
				if (this.uiQuery.getRideOpsUiV2OptIn()) {
					return;
				}
				this.uiService.setRideOpsUiV2OptIn(true);
			},
		);
	};
}
